import React, { useState, useRef, useEffect } from 'react';
import { TextField, Box, Typography } from '@mui/material';

interface ExcelLikeFieldProps {
  value: string;
  onSave: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
}

const ExcelLikeField: React.FC<ExcelLikeFieldProps> = ({
  value,
  onSave,
  placeholder = '',
  multiline = false,
  rows = 1
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Update the internal state when the external value changes
  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  // Focus the input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (currentValue !== value) {
      onSave(currentValue);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      setIsEditing(false);
      onSave(currentValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setCurrentValue(value); // Revert to original value
      setIsEditing(false);
    }
  };

  return (
    <Box 
      onClick={!isEditing ? handleClick : undefined}
      sx={{ 
        width: '100%', 
        minHeight: '32px',
        border: isEditing ? 'none' : '1px solid transparent',
        p: isEditing ? 0 : 1,
        cursor: isEditing ? 'text' : 'pointer',
        '&:hover': {
          backgroundColor: !isEditing ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
          border: !isEditing ? '1px dashed #ccc' : 'none'
        }
      }}
    >
      {isEditing ? (
        <TextField
          inputRef={inputRef}
          variant="outlined"
          size="small"
          fullWidth
          value={currentValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus
          multiline={multiline}
          rows={rows}
          sx={{ width: '100%' }}
        />
      ) : (
        <Typography
          variant="body2"
          sx={{ 
            whiteSpace: 'pre-wrap',
            minHeight: '24px',
            color: currentValue ? 'text.primary' : 'text.secondary'
          }}
        >
          {currentValue || placeholder}
        </Typography>
      )}
    </Box>
  );
};

export default ExcelLikeField;